import React, { useState, useCallback } from "react";
import { HA_DOMAINS, isEntityOn, HVAC_MODES } from "./haConstants";

/* ══ ENTITY CARD RENDERERS & CONTROLS ═════════════════════
   Per-domain display cards and interactive control panels.
   EntityCard — compact tile (icon, name, state).
   EntityControlPanel — expanded controls (sliders, toggles,
   temperature pickers) shown when user taps an entity.     */

/* ── Domain icons (inline SVG, 18x18) ── */
const ICONS = {
  light:   <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.3"><path d="M9 2v1M3.3 5.3l.7.7M14.7 5.3l-.7.7M2 10h1M15 10h1"/><path d="M6 13a4 4 0 116 0"/><path d="M7 13v2h4v-2"/></svg>,
  switch:  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.3"><rect x="2" y="5" width="14" height="8" rx="4"/><circle cx="12" cy="9" r="2.5"/></svg>,
  climate: <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.3"><path d="M9 2v10"/><circle cx="9" cy="14" r="2.5"/><path d="M6.5 14a2.5 2.5 0 015 0"/><path d="M11 6V3H7v3"/></svg>,
  lock:    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.3"><rect x="4" y="8" width="10" height="8" rx="1.5"/><path d="M6.5 8V6a2.5 2.5 0 015 0v2"/><circle cx="9" cy="12" r="1" fill="currentColor" stroke="none"/></svg>,
  cover:   <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.3"><rect x="3" y="3" width="12" height="12" rx="1"/><path d="M3 6h12M3 9h12"/></svg>,
  fan:     <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.3"><circle cx="9" cy="9" r="1.5"/><path d="M9 7.5C9 4 7 2 5 3s0 5 0 5"/><path d="M10.5 9c3.5 0 5.5-2 4.5-4s-5 0-5 0"/><path d="M9 10.5c0 3.5 2 5.5 4 4.5s0-5 0-5"/><path d="M7.5 9c-3.5 0-5.5 2-4.5 4s5 0 5 0"/></svg>,
  sensor:  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.3"><circle cx="9" cy="9" r="6"/><path d="M9 5v4l2.5 2.5"/></svg>,
  alarm:   <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.3"><path d="M9 2l7 5v7H2V7l7-5z"/><circle cx="9" cy="10" r="2"/><path d="M9 8v2"/></svg>,
  media:   <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.3"><rect x="2" y="4" width="14" height="10" rx="1.5"/><path d="M7.5 7.5l4 2.5-4 2.5z"/></svg>,
  camera:  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.3"><rect x="2" y="5" width="14" height="10" rx="1.5"/><circle cx="9" cy="10" r="2.5"/><path d="M6 5l1-2h4l1 2"/></svg>,
  vacuum:  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.3"><circle cx="9" cy="9" r="6"/><circle cx="9" cy="9" r="2"/></svg>,
  weather: <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.3"><circle cx="7" cy="7" r="3"/><path d="M12 10a3 3 0 01-3 3H5a2.5 2.5 0 010-5 3 3 0 016 0h1z"/></svg>,
  default: <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.3"><circle cx="9" cy="9" r="6"/><path d="M9 6v3h3"/></svg>,
};

export function entityIcon(domain) {
  const domInfo = HA_DOMAINS[domain];
  return ICONS[domInfo?.icon] || ICONS[domain] || ICONS.default;
}

/* ── State display text ── */
function stateText(entity, isRTL) {
  const s = entity.state;
  const attrs = entity.attributes || {};
  const domain = entity.domain;

  if (domain === "climate") {
    const temp = attrs.current_temperature;
    const target = attrs.temperature;
    const unit = attrs.temperature_unit || "°C";
    if (temp != null && target != null) return `${temp}${unit} → ${target}${unit}`;
    if (temp != null) return `${temp}${unit}`;
    return s;
  }

  if (domain === "sensor" || domain === "binary_sensor") {
    const unit = attrs.unit_of_measurement;
    if (unit) return `${s} ${unit}`;
    if (domain === "binary_sensor") {
      const cls = attrs.device_class;
      if (cls === "door" || cls === "window" || cls === "opening" || cls === "garage_door") {
        return s === "on" ? (isRTL ? "مفتوح" : "Open") : (isRTL ? "مغلق" : "Closed");
      }
      if (cls === "motion") return s === "on" ? (isRTL ? "حركة" : "Motion") : (isRTL ? "صافي" : "Clear");
      return s === "on" ? (isRTL ? "نشط" : "Active") : (isRTL ? "خامل" : "Inactive");
    }
    return s;
  }

  if (domain === "lock") {
    return s === "locked" ? (isRTL ? "مقفل" : "Locked") : (isRTL ? "مفتوح" : "Unlocked");
  }

  if (domain === "cover") {
    const pos = attrs.current_position;
    if (pos != null) return `${pos}%`;
    return s === "open" ? (isRTL ? "مفتوح" : "Open") : s === "closed" ? (isRTL ? "مغلق" : "Closed") : s;
  }

  if (domain === "light") {
    if (s === "off") return isRTL ? "مطفأ" : "Off";
    const bri = attrs.brightness;
    if (bri != null) return `${Math.round(bri / 255 * 100)}%`;
    return isRTL ? "مشتعل" : "On";
  }

  if (domain === "media_player") {
    if (s === "playing") {
      const title = attrs.media_title;
      return title || (isRTL ? "تشغيل" : "Playing");
    }
    return s === "off" ? (isRTL ? "مطفأ" : "Off") : s;
  }

  if (s === "on") return isRTL ? "مشغل" : "On";
  if (s === "off") return isRTL ? "مطفأ" : "Off";
  return s;
}

/* ── Entity card component ── */
export function EntityCard({ entity, isRTL, customLabel, onClick }) {
  const on = isEntityOn(entity);
  const domain = entity.domain;
  const name = isRTL && customLabel?.ar ? customLabel.ar : (customLabel?.en || entity.friendly_name);
  const icon = entityIcon(domain);
  const state = stateText(entity, isRTL);
  const domInfo = HA_DOMAINS[domain];

  return (
    <div
      className={`sh-ecard${on ? " sh-ecard-on" : ""}`}
      onClick={onClick}
      role={domInfo?.controllable ? "button" : undefined}
      tabIndex={domInfo?.controllable ? 0 : undefined}
    >
      <div className="sh-ecard-icon">{icon}</div>
      <div className="sh-ecard-info">
        <div className="sh-ecard-name">{name}</div>
        <div className="sh-ecard-state">{state}</div>
      </div>
    </div>
  );
}

/* ── Area summary (counts for dashboard stats) ── */
export function areaSummary(entities) {
  let lightsOn = 0, lightsTotal = 0, locksUnlocked = 0, locksTotal = 0;
  for (const e of entities) {
    if (e.domain === "light") { lightsTotal++; if (isEntityOn(e)) lightsOn++; }
    if (e.domain === "lock") { locksTotal++; if (e.state === "unlocked") locksUnlocked++; }
  }
  return { lightsOn, lightsTotal, locksUnlocked, locksTotal };
}

/* ══════════════════════════════════════════════════════════
   ENTITY CONTROL PANEL — per-domain interactive controls
   ══════════════════════════════════════════════════════════ */

/* ── Light controls: toggle + brightness slider ── */
function LightControl({ entity, callService, isRTL }) {
  const on = isEntityOn(entity);
  const attrs = entity.attributes || {};
  const brightness = attrs.brightness != null ? Math.round(attrs.brightness / 255 * 100) : null;
  const supportsBrightness = attrs.supported_color_modes?.some(m => m !== "onoff") ?? brightness != null;

  const toggle = useCallback(() => {
    callService("light", "toggle", entity.entity_id);
  }, [callService, entity.entity_id]);

  const setBrightness = useCallback((pct) => {
    callService("light", "turn_on", entity.entity_id, { brightness: Math.round(pct / 100 * 255) });
  }, [callService, entity.entity_id]);

  return (
    <div className="sh-ctrl">
      <div className="sh-ctrl-row">
        <span className="sh-ctrl-label">{isRTL ? "الإضاءة" : "Light"}</span>
        <button className={`sh-toggle${on ? " sh-toggle-on" : ""}`} onClick={toggle}>
          <div className="sh-toggle-knob" />
        </button>
      </div>
      {supportsBrightness && on && (
        <div className="sh-ctrl-row">
          <span className="sh-ctrl-label">{isRTL ? "السطوع" : "Brightness"}</span>
          <input
            type="range" min="1" max="100"
            value={brightness ?? 100}
            className="sh-slider"
            onChange={e => setBrightness(Number(e.target.value))}
          />
          <span className="sh-ctrl-val">{brightness ?? 100}%</span>
        </div>
      )}
    </div>
  );
}

/* ── Switch / fan controls: toggle ── */
function SwitchControl({ entity, callService, isRTL }) {
  const on = isEntityOn(entity);
  const toggle = useCallback(() => {
    callService(entity.domain, "toggle", entity.entity_id);
  }, [callService, entity.domain, entity.entity_id]);

  return (
    <div className="sh-ctrl">
      <div className="sh-ctrl-row">
        <span className="sh-ctrl-label">{entity.friendly_name}</span>
        <button className={`sh-toggle${on ? " sh-toggle-on" : ""}`} onClick={toggle}>
          <div className="sh-toggle-knob" />
        </button>
      </div>
    </div>
  );
}

/* ── Climate controls: target temp + HVAC mode ── */
function ClimateControl({ entity, callService, isRTL }) {
  const attrs = entity.attributes || {};
  const target = attrs.temperature;
  const unit = attrs.temperature_unit || "°C";
  const current = attrs.current_temperature;
  const mode = entity.state;
  const modes = attrs.hvac_modes || HVAC_MODES.filter(m => m === "off" || m === mode);
  const step = attrs.target_temp_step || 0.5;
  const min = attrs.min_temp || 16;
  const max = attrs.max_temp || 30;

  const setTemp = useCallback((t) => {
    const clamped = Math.min(max, Math.max(min, t));
    callService("climate", "set_temperature", entity.entity_id, { temperature: clamped });
  }, [callService, entity.entity_id, min, max]);

  const setMode = useCallback((m) => {
    callService("climate", "set_hvac_mode", entity.entity_id, { hvac_mode: m });
  }, [callService, entity.entity_id]);

  const HVAC_LABELS = { off: isRTL ? "إيقاف" : "Off", heat: isRTL ? "تدفئة" : "Heat", cool: isRTL ? "تبريد" : "Cool", heat_cool: "Auto", auto: "Auto", dry: isRTL ? "تجفيف" : "Dry", fan_only: isRTL ? "مروحة" : "Fan" };

  return (
    <div className="sh-ctrl">
      {current != null && (
        <div className="sh-ctrl-row">
          <span className="sh-ctrl-label">{isRTL ? "الحالية" : "Current"}</span>
          <span className="sh-ctrl-val">{current}{unit}</span>
        </div>
      )}
      {target != null && (
        <div className="sh-ctrl-row">
          <span className="sh-ctrl-label">{isRTL ? "الهدف" : "Target"}</span>
          <div className="sh-temp-ctrl">
            <button className="sh-temp-btn" onClick={() => setTemp(target - step)}>−</button>
            <span className="sh-temp-val">{target}{unit}</span>
            <button className="sh-temp-btn" onClick={() => setTemp(target + step)}>+</button>
          </div>
        </div>
      )}
      {modes.length > 1 && (
        <div className="sh-ctrl-row">
          <span className="sh-ctrl-label">{isRTL ? "الوضع" : "Mode"}</span>
          <div className="sh-mode-pills">
            {modes.map(m => (
              <button key={m} className={`sh-mode-pill${m === mode ? " sh-mode-active" : ""}`} onClick={() => setMode(m)}>
                {HVAC_LABELS[m] || m}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Lock control: toggle with confirmation ── */
function LockControl({ entity, callService, isRTL }) {
  const [confirming, setConfirming] = useState(false);
  const locked = entity.state === "locked";

  const doAction = useCallback(() => {
    callService("lock", locked ? "unlock" : "lock", entity.entity_id);
    setConfirming(false);
  }, [callService, entity.entity_id, locked]);

  return (
    <div className="sh-ctrl">
      <div className="sh-ctrl-row">
        <span className="sh-ctrl-label">{locked ? (isRTL ? "مقفل" : "Locked") : (isRTL ? "مفتوح" : "Unlocked")}</span>
        {!confirming ? (
          <button className={`sh-lock-btn${locked ? "" : " sh-lock-warn"}`} onClick={() => setConfirming(true)}>
            {locked ? (isRTL ? "فتح القفل" : "Unlock") : (isRTL ? "قفل" : "Lock")}
          </button>
        ) : (
          <div className="sh-confirm-row">
            <button className="sh-lock-btn sh-lock-confirm" onClick={doAction}>
              {isRTL ? "تأكيد" : "Confirm"}
            </button>
            <button className="sh-lock-btn" onClick={() => setConfirming(false)}>
              {isRTL ? "إلغاء" : "Cancel"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Cover controls: open / stop / close + position slider ── */
function CoverControl({ entity, callService, isRTL }) {
  const attrs = entity.attributes || {};
  const pos = attrs.current_position;

  const cmd = useCallback((svc) => {
    callService("cover", svc, entity.entity_id);
  }, [callService, entity.entity_id]);

  const setPos = useCallback((p) => {
    callService("cover", "set_cover_position", entity.entity_id, { position: p });
  }, [callService, entity.entity_id]);

  return (
    <div className="sh-ctrl">
      <div className="sh-ctrl-row">
        <span className="sh-ctrl-label">{isRTL ? "الغطاء" : "Cover"}</span>
        <div className="sh-cover-btns">
          <button className="sh-cover-btn" onClick={() => cmd("open_cover")} title={isRTL?"فتح":"Open"}>▲</button>
          <button className="sh-cover-btn" onClick={() => cmd("stop_cover")} title={isRTL?"توقف":"Stop"}>■</button>
          <button className="sh-cover-btn" onClick={() => cmd("close_cover")} title={isRTL?"إغلاق":"Close"}>▼</button>
        </div>
      </div>
      {pos != null && (
        <div className="sh-ctrl-row">
          <span className="sh-ctrl-label">{isRTL ? "الموضع" : "Position"}</span>
          <input type="range" min="0" max="100" value={pos} className="sh-slider" onChange={e => setPos(Number(e.target.value))} />
          <span className="sh-ctrl-val">{pos}%</span>
        </div>
      )}
    </div>
  );
}

/* ── Main control panel dispatcher ── */
export function EntityControlPanel({ entity, callService, isRTL }) {
  if (!entity || !callService) return null;
  const d = entity.domain;
  if (d === "light") return <LightControl entity={entity} callService={callService} isRTL={isRTL} />;
  if (d === "switch" || d === "fan") return <SwitchControl entity={entity} callService={callService} isRTL={isRTL} />;
  if (d === "climate" || d === "water_heater" || d === "humidifier") return <ClimateControl entity={entity} callService={callService} isRTL={isRTL} />;
  if (d === "lock") return <LockControl entity={entity} callService={callService} isRTL={isRTL} />;
  if (d === "cover") return <CoverControl entity={entity} callService={callService} isRTL={isRTL} />;
  return null;
}
